import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  ConvertUnitsApi,
  ConvertUnitValue,
  MassUnit,
  ModalRef,
  ModalService,
  ReactiveComponent,
  SelectItemV2,
  TwoButtonModalV2Component,
  UnitsService,
  ModalModule,
  InputV2Module,
  IconV2Module,
  ButtonV2Module,
  CheckboxV2Module,
} from '@schenck/rotec-components';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  greaterZeroValidator,
  valueRangeValidator,
} from 'src/pages/rotor-type-details/rotor-type-details.helper';
import { UnitsHelperService } from 'src/services/units-helper/units-helper.service';
import { OVERWRITE_WEIGHTS_MODAL } from 'src/utils/constants/modal.const';
import { WeightSetItem } from 'src/utils/interfaces/rotor-type.interfaces';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';

@Component({
  selector: 'weight-sets-modal',
  templateUrl: './weight-sets-modal.component.html',
  styleUrls: ['./weight-sets-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalModule,
    TranslatePipe,
    TranslateDirective,
    InputV2Module,
    IconV2Module,
    ButtonV2Module,
    CheckboxV2Module,
    AsyncPipe,
  ],
})
export class WeightSetsModalComponent extends ReactiveComponent {
  private readonly modalRef = inject<
    ModalRef<{
      weights: WeightSetItem[];
      defaultMassUnit: MassUnit;
    }>
  >(ModalRef);
  private readonly unitsHelperService = inject(UnitsHelperService);
  private readonly unitsService = inject(UnitsService);
  private readonly convertUnitsApi = inject(ConvertUnitsApi);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(ModalService);

  public width: string = '1280px';
  public height: string = '780px';
  public formGroup: UntypedFormGroup;
  public disableOkButton$ = new BehaviorSubject<boolean>(false);
  public createWeightsProgress$ = new BehaviorSubject<boolean>(false);
  public incrementValidationChange$ = new BehaviorSubject<boolean>(false);

  constructor() {
    super();
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.formGroup = new UntypedFormGroup({
      manageWeights: new UntypedFormGroup({
        numWeights: new UntypedFormControl(8, {
          validators: [Validators.required, valueRangeValidator({ min: 1, max: 50 })],
        }),
        increment: new UntypedFormGroup({
          value: new UntypedFormControl(null, [Validators.required]),
          unit: new UntypedFormControl(this.modalRef.data.defaultMassUnit),
        }),
      }),
      weights: new UntypedFormArray([], this.sameWeightValidator),
    });

    this._setIncrementValidator();
    // setting the increment value this way, because we need to trigger the valuechanges subscription
    this.formGroup.get('manageWeights.increment.value').setValue(1.5);

    // The weights in the weightSet are in SI Unit and Value, therefore we need to convert them
    // to their original unit and value
    if (this.modalRef.data?.weights?.length > 0) {
      this.formGroup.get('manageWeights.numWeights').setValue(this.modalRef.data.weights.length);
      this.formGroup
        .get('manageWeights.increment.unit')
        .setValue(this.modalRef.data.weights[0].mass.value.orgUnit.symbol);

      this.unitsHelperService
        .getWeightsWithOrigin$(this.modalRef.data.weights)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((sortedWeights) => {
          this.formGroup
            .get('manageWeights.increment.value')
            .setValue(sortedWeights[0].mass.orgValue);

          sortedWeights.forEach((weight) => {
            this.addWeight(
              weight.weightName.value,
              weight.mass.orgValue,
              weight.mass.value.orgUnit.symbol,
              weight.useWeightForCorrection.value,
              weight.mass.value.siValue
            );
          });

          this.cdr.detectChanges();
        });
    }
  }

  public get hasManualChanges(): boolean {
    // Definition of manual changes:
    // 0. If there are no weights, don't show the warning
    if (this.weights.value.length === 0) {
      return false;
    }
    // 1. If the number of weights is different from the number of weights in the form
    const numWeights = this.formGroup.get('manageWeights.numWeights').value;
    const numRows = this.formGroup.get('weights').value.length;
    if (numWeights !== numRows) {
      return true;
    }
    // 2. If the number of weights is the same but the weight name, value or unit is different
    if (
      typeof this.formGroup.get('weights').value.find((item, idx) => {
        // 2.1 If the weight name is different
        const diffName = item.weight.name !== `${idx + 1}`;
        // 2.2 If the weight value is different
        const diffValue =
          item.weight.value !==
          this.formGroup.get('manageWeights.increment.value').value * (idx + 1);
        // 2.3 If the weight unit is different
        const diffUnit =
          item.weight.unit !== this.formGroup.get('manageWeights.increment.unit').value;
        // 3. If the weight is not active
        return diffName || diffValue || diffUnit || !item.active;
      }) !== 'undefined'
    ) {
      return true;
    }

    return false;
  }

  public onChangeNumWeights(value: number): void {
    this.formGroup.get('manageWeights.numWeights').setValue(value);
  }

  public onChangeIncrement(value: SelectItemV2<number>): void {
    this.formGroup.get('manageWeights.increment.value').setValue(value.value);
    this.formGroup.get('manageWeights.increment.unit').setValue(value.unit);
  }

  public createWeights(): void {
    // Prompt the user to confirm the creation of new weights
    if (this.hasManualChanges) {
      this.modalService
        .open(TwoButtonModalV2Component, OVERWRITE_WEIGHTS_MODAL)
        .events$.pipe(
          takeUntil(this.onDestroy$),
          filter((confirm) => !!confirm)
        )
        .subscribe(() => this._createWeights());
    } else {
      this._createWeights();
    }
  }

  public get okButtonDisabled(): boolean {
    return !this.weights?.length || !this.weights?.valid;
  }

  public toggleActive(control: UntypedFormControl): void {
    control.get('active').setValue(!control.get('active').value);
  }

  public onNameChange(value: string, control): void {
    control.get('weight.name').setValue(value);
  }

  public onWeightChange(value: SelectItemV2<number>, control: UntypedFormControl): void {
    control.get('weight.value').setValue(value.value);
    control.get('weight.unit').setValue(value.unit);
  }

  public get weights(): UntypedFormArray {
    return this.formGroup?.get('weights') as UntypedFormArray;
  }

  public addWeight(
    name: string,
    weight: number,
    unit: string,
    active: boolean = true,
    siValue?: number
  ): void {
    const newWeight = new UntypedFormGroup({
      active: new UntypedFormControl(active),
      weight: new UntypedFormGroup({
        name: new UntypedFormControl(name, [Validators.required]),
        value: new UntypedFormControl(weight, [Validators.required, greaterZeroValidator]),
        unit: new UntypedFormControl(
          unit || this.formGroup.get('manageWeights.increment.unit').value,
          [Validators.required]
        ),
        siValue: new UntypedFormControl(siValue),
      }),
    });

    // When adding a new weight, we need to make sure that everytime we change the value, we also update the SIValue
    // this is only possible with a valueChanges subscription, and convert request
    // We need to do this because we have to validate, that there are not two of the same weights, and we can only do this
    // if we compare them by their SIValue
    newWeight
      .get('weight.value')
      .valueChanges.pipe(
        takeUntil(this.onDestroy$),
        tap(() => this.disableOkButton$.next(true)),
        debounceTime(300),
        switchMap(() => {
          if (
            newWeight.get('weight.value').value &&
            (newWeight.get('weight.unit').value !== 'kg' ||
              (newWeight.get('weight.unit').value === 'kg' &&
                newWeight.get('weight.value').value !== newWeight.get('weight.siValue').value))
          ) {
            return this.unitsHelperService.convertUnit$(
              {
                ...newWeight.get('weight').value,
                name: newWeight.get('weight').value.name || 'Weight',
              } as SelectItemV2<number>,
              'Mass',
              'kg'
            );
          } else {
            return of(newWeight.get('weight.value').value);
          }
        }),
        finalize(() => this.disableOkButton$.next(false))
      )
      .subscribe((value) => {
        newWeight.get('weight.siValue').setValue(value);
        this.formGroup.updateValueAndValidity();
        this.cdr.detectChanges();
        this.disableOkButton$.next(false);
      });

    // if no SIValue was added, we need to trigger valuechanges once to get the SIValue
    if (!siValue) {
      newWeight.get('weight.value').setValue(newWeight.get('weight.value').value);
    }

    this.weights.push(newWeight);
    this.formGroup.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  /**
   * Save the weights and close the modal
   * When we save the weights, we don't need to convert them to SI Unit, because we already did
   * with the valuechanges subscription.
   * The next step is to transform the formgroup to the correct WeightSet format (which is also the same format used to store
   * the weights in the database)
   */
  public save(): void {
    const weights: WeightSetItem[] = this.weights
      .getRawValue()
      .sort((a, b) => a.weight.siValue - b.weight.siValue)
      .map((item, index) => {
        return {
          weightIndex: index,
          useWeightForCorrection: {
            value: item.active,
            isEdited: true,
          },
          weightName: {
            value: item.weight.name,
            isEdited: true,
          },
          mass: {
            name: item.weight.name,
            isEdited: true,
            orgValue: item.weight.value,
            value: {
              siValue: item.weight.siValue,
              orgUnit: {
                symbol: item.weight.unit,
                unitIndex: this.unitsService.getUnitIndex(item.weight.unit, 'Mass'),
              },
              siUnit: { symbol: 'kg', unitIndex: this.unitsService.getUnitIndex('kg', 'Mass') },
            },
          },
        };
      });

    this.modalRef.emitAndClose(weights);
  }

  public cancel(): void {
    this.modalRef.close();
  }

  public deleteWeight(index: number): void {
    this.weights.removeAt(index);
    this.formGroup.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  /**
   * Validate by checking if there are two of the same weights (compared by SI Value)
   */
  private sameWeightValidator(weights: UntypedFormArray): ValidationErrors | null {
    const weightValues = weights.controls.map((control) => control.get('weight.siValue').value);
    const uniqueValues = new Set(weightValues);

    const duplicates = weightValues.filter((value, index, self) => self.indexOf(value) !== index);

    weights.controls.forEach((control) => {
      const value = control.get('weight.siValue').value;
      if (duplicates.includes(value)) {
        control.get('weight.value').setErrors({ duplicate: true });
      } else {
        const errors = control.get('weight.value').errors;
        if (errors) {
          delete errors.duplicate;
          control.get('weight.value').setErrors(Object.keys(errors).length ? errors : null);
        }
      }
    });

    return weightValues.length !== uniqueValues.size ? { sameWeight: true } : null;
  }

  private _createWeights(): void {
    this.createWeightsProgress$.next(true);
    this.weights.clear();
    const numWeights = this.formGroup.get('manageWeights.numWeights').value;
    const increment = this.formGroup.get('manageWeights.increment.value').value;
    const unit = this.formGroup.get('manageWeights.increment.unit').value;

    // Before adding the weights via the createWeights button to the formgroup, we need to convert them to the SI Unit
    // This allows us to convert all values with a single API Request (because all units are the same)
    const incrementValues = Array.from({ length: numWeights }, (_, i) => increment * (i + 1));

    of(null)
      .pipe(
        take(1),
        switchMap(() => {
          if (unit !== 'kg') {
            return this.convertUnitsApi.convertUnits$({
              group: `EPhysUnit_Mass`,
              decimalDigits: 10,
              originUnit: this.unitsService.getUnitIndex(unit, 'Mass'),
              targetUnit: this.unitsService.getUnitIndex('kg', 'Mass'),
              values: incrementValues.map((value, index) => ({
                key: index.toString(),
                value,
              })),
            });
          }
          return of(incrementValues);
        }),
        catchError((err) => {
          this.createWeightsProgress$.next(false);
          throw err;
        }),
        finalize(() => this.createWeightsProgress$.next(false))
      )
      .subscribe((values: ConvertUnitValue[]) => {
        for (let i = 0; i < numWeights; i++) {
          this.addWeight(
            `${i + 1}`,
            incrementValues[i],
            unit,
            true,
            unit === 'kg'
              ? incrementValues[i]
              : values.find((item) => item.key === i.toString()).convertedValue
          );
        }
      });
  }

  /**
   * Set the increment validator based on the unit
   * The upper limit of the increment needs to be converted based
   * on the selected unit. In case it is kg, we don't need to convert it (upper limit is 1 kg)
   */
  private _setIncrementValidator(): void {
    this.formGroup
      .get('manageWeights.increment.unit')
      .valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => this.incrementValidationChange$.next(true)),
        takeUntil(this.onDestroy$),
        debounceTime(300),
        switchMap((unit) => {
          if (unit === 'kg') {
            return of(1);
          } else {
            // If Unit is not kg, we need to convert the upper limit first to the target unit
            return this.unitsHelperService.convertUnit$(
              { value: 1, unit: 'kg', name: 'Weight' },
              'Mass',
              unit
            );
          }
        }),
        catchError((err) => {
          this.incrementValidationChange$.next(false);
          throw err;
        }),
        finalize(() => this.incrementValidationChange$.next(false))
      )
      .subscribe((max) => {
        this.incrementValidationChange$.next(false);
        this.formGroup
          .get('manageWeights.increment.value')
          .setValidators([
            Validators.required,
            valueRangeValidator({ min: 0, max }),
            greaterZeroValidator,
          ]);

        this.formGroup.get('manageWeights.increment.value').updateValueAndValidity();
        this.cdr.detectChanges();
      });
  }
}
